import { useState } from "react";
import { useMount } from "react-use";

import DataReference from "metabase/query_builder/components/dataref/DataReference";
import { SnippetSidebar } from "metabase/query_builder/components/template_tags/SnippetSidebar";
import { Box } from "metabase/ui";
import type Question from "metabase-lib/v1/Question";
import type { NativeQuerySnippet } from "metabase-types/api";

type EditorSidebarProps = {
  question: Question;
  isDataReferenceOpen: boolean;
  isSnippetSidebarOpen: boolean;
  onToggleDataReference: () => void;
  onToggleSnippetSidebar: () => void;
  onChangeModalSnippet: (snippet: NativeQuerySnippet) => void;
  onInsertSnippet: (snippet: NativeQuerySnippet) => void;
};

/** Data reference / snippet sidebar for one query tab. */
export function EditorSidebar({
  isDataReferenceOpen,
  isSnippetSidebarOpen,
  ...props
}: EditorSidebarProps) {
  if (!isDataReferenceOpen && !isSnippetSidebarOpen) {
    return null;
  }
  return (
    <Box h="100%" data-testid="editor-sidebar">
      {isSnippetSidebarOpen ? (
        <SnippetSidebar
          onClose={props.onToggleSnippetSidebar}
          setModalSnippet={props.onChangeModalSnippet}
          openSnippetModalWithSelectedText={props.onChangeModalSnippet}
          snippetCollectionId={null}
          insertSnippet={props.onInsertSnippet}
        />
      ) : (
        <DataReferenceSidebar
          question={props.question}
          onClose={props.onToggleDataReference}
        />
      )}
    </Box>
  );
}

type DataReferenceStackItem = { type: string; item: { id: unknown } };

function DataReferenceSidebar({
  question,
  onClose,
}: {
  question: Question;
  onClose: () => void;
}) {
  const [stack, setStack] = useState<DataReferenceStackItem[]>([]);

  useMount(() => {
    const databaseId = question.databaseId();
    if (stack.length === 0 && databaseId != null) {
      setStack([{ type: "database", item: { id: databaseId } }]);
    }
  });

  return (
    <DataReference
      dataReferenceStack={stack}
      popDataReferenceStack={() => setStack((prev) => prev.slice(0, -1))}
      pushDataReferenceStack={(ref: DataReferenceStackItem) =>
        setStack((prev) => [...prev, ref])
      }
      onClose={onClose}
    />
  );
}
